// Копирайт уз — spec/08_copy.md, раздел «Узы» (без пула комплиментов — он в copy/pools.ts).
//
// Голос — вариант A «своя в чате» (docs/copy-variants.md), КРОМЕ всего, что касается лучика:
// сам лучик, комплимент и совет отправить тёплые слова написаны ясно и тепло, без мема
// (выбор основателя). Лучик — единственный жест, которым человек говорит с близким; шутка
// в этот момент читается как шум.
// `break` — зарезервированное слово, поэтому секция объявлена как `breakCopy` и переименована
// обратно в `break` при сборке итогового объекта `copy` (см. copy/index.ts).
//
// Пол близкого. Луня говорит о человеке в прошедшем времени («не показывалась», «убрала
// телефон»), и без пола все эти фразы уезжали в женский род — «Дима не показывалась 3 дня».
// Пол приходит из связи (`link.partner.gender`), фраза собирается одним шаблоном
// `{мужская|женская}`; если пол не указан, берётся отдельная нейтральная формулировка.
import { INVITE_TTL_DAYS, textByGender } from '@lunya/contracts'

import {
  DAY_FORMS,
  LINK_FORMS,
  LINK_GENITIVE_FORMS,
  pluralizeRu,
  pluralizeWithCount,
} from '~/shared/utils'

import type {
  BreakCopy,
  InboxCopy,
  LinkCopy,
  LinksCopy,
  NodeCopy,
  PersonCopy,
  RayCopy,
} from './types'

export const links: LinksCopy = {
  title: 'Твои узы',
  // `n` — все узы человека, `limit` — сколько он может позвать сам. Обычно первое не больше
  // второго, и тогда счётчик читается как в спеке: «1 из 5 связей». Но гостевая уза (вошёл по
  // чужой ссылке) слот не занимает (04_links.md §2.9), поэтому уз может оказаться больше лимита,
  // и «6 из 5 связей» было бы враньём про обе половины. В этом случае знаменатель убираем:
  // число уз остаётся правдой, а про лимит расскажет шторка, когда он действительно упрётся.
  counter: ({ n, limit }) =>
    n > limit
      ? pluralizeWithCount(n, LINK_FORMS)
      : `${n} из ${limit} ${pluralizeRu(limit, LINK_GENITIVE_FORMS)}`,
  // Вход в ролик про узы. Формулировка вопросом, а не «Инструкция» или «Обучение»: человек
  // нажимает на неё ровно тогда, когда этот вопрос у него и возник.
  how_link: 'Как это работает?',
  how_title: 'Как работают узы',
  how_close: 'Понятно, спасибо!',
  // Уза исчезла, пока экран был открыт: чаще всего это значит, что человек удалил аккаунт.
  // Настаивать на причине нельзя — её мы не знаем, — но и вечный спиннер вместо ответа
  // человек не заслужил (правка основателя про удаление аккаунта).
  gone_title: 'Этой узы больше нет',
  gone_body:
    'Может, человек удалил свой аккаунт, а может, узу разорвали. На полотне её уже не видно.',
  gone_cta: 'Вернуться к узам',
  advice_overline: 'СОВЕТ',
  advice: ({ name }) => `Отправь пару тёплых слов для ${name}`,
  add: 'Связь',
}

export const node: NodeCopy = {
  together: ({ n }) => `вместе ${pluralizeWithCount(n, DAY_FORMS)}`,
  // Оверлайн над недельной полосой. Раньше был «ЕЁ НЕДЕЛЯ» — на мужском узле это промах,
  // а склонять негде: строку читает шит узла, который сейчас правит другая задача.
  // Без притяжательного оверлайн одинаково верен для всех: шит и так открыт на конкретном человеке.
  week: 'НЕДЕЛЯ',
  marked: ({ when, gender }) =>
    textByGender(gender, `отмечал{ся|ась} ${when}`, `последняя отметка ${when}`),
  marked_never: ({ gender }) =>
    textByGender(gender, 'давно не отмечал{ся|ась}', 'давно нет отметок'),
  ray: 'Отправить лучик ✨',
  write: 'Написать в TG',
  more: 'Подробнее',
}

export const person: PersonCopy = {
  tender: ({ name }) => `У ${name} сегодня тихо — лучик будет кстати.`,
  beacon: ({ name, n, gender }) => {
    const days = pluralizeWithCount(n, DAY_FORMS)
    return textByGender(
      gender,
      `${name} не показывал{ся|ась} ${days}. Загляни к {нему|ней} по-настоящему.`,
      `${name} не отмечается ${days}. Загляни по-настоящему, не через приложение.`,
    )
  },
  beacon_cta: ({ gender }) => textByGender(gender, 'Написать {ему|ей}', 'Написать в Telegram'),
  vacation: ({ name, date, gender }) =>
    textByGender(
      gender,
      `${name} убрал{|а} телефон подальше до ${date}. Всё в порядке, я за {ним|ней} приглядываю.`,
      `${name} убирает телефон подальше до ${date}. Всё в порядке, я приглядываю.`,
    ),
  vacation_note: ({ gender }) =>
    textByGender(
      gender,
      'маячок по {нему|ней} молчит до конца отпуска',
      'маячок молчит до конца отпуска',
    ),
}

export const ray: RayCopy = {
  title: ({ name }) => `Лучик для ${name}`,
  sub: 'можно добавить пару слов',
  overline: 'КОМПЛИМЕНТ · МОЖНО ПРОПУСТИТЬ',
  custom: 'Написать свой…',
  // Небесная метафора убрана по всему продукту (правка основателя): «его небо будет
  // светиться» требовало от читателя знать, что небо — это полотно уз. Рода в новой
  // формулировке нет вовсе, поэтому пара gendered/neutral тут больше ничего не различает.
  note: () => 'Тебе один клик, а лучик будет радовать близкого ещё 6 часов.',
  send: 'Отправить ✨',
  delivered: ({ gender }) =>
    textByGender(gender, 'Лучик долетел до {него|неё} ✨', 'Лучик долетел ✨'),
}

export const link: LinkCopy = {
  new_title: 'Кто это для тебя?',
  new_body: 'От этого зависит, каким тоном я о нём говорю и как быстро начинаю волноваться.',
  new_note: 'Ссылка одноразовая: сработает только у того, кому ты её отправишь.',
  new_cta: 'Отправить приглашение',
  waiting: 'ждём ответа',
  waiting_body: ({ when }) =>
    `Приглашение улетело ${when}. Ссылка живёт ${pluralizeWithCount(INVITE_TTL_DAYS, DAY_FORMS)}, потом погаснет сама.`,
  resend: 'Отправить ещё раз',
  revoke: 'Отозвать',
  taken: ({ name }) => `Нить уже занята — попроси у ${name} новую`,
  result: {
    accepted_cta: 'Показать узы',

    already_title: 'Вы уже вместе',
    already_body: ({ inviter, gender }) =>
      inviter
        ? textByGender(
            gender,
            `Нить с ${inviter} на месте — ссылка сработала раньше. Загляни, как {он|она}.`,
            `Нить с ${inviter} на месте — ссылка сработала раньше. Загляни, как дела.`,
          )
        : 'Нить на месте — ссылка сработала раньше.',

    // Одноразовость ссылки — закрытое решение (04 §0.1). Формулировка та же, что в шите узла.
    taken_title: 'Эта нить уже занята',
    taken_body: ({ inviter }) =>
      inviter
        ? `Ссылка одноразовая, и по ней уже прошёл кто-то другой. Попроси у ${inviter} новую — это одна кнопка.`
        : 'Ссылка одноразовая, и по ней уже прошёл кто-то другой. Попроси новую — это одна кнопка.',

    expired_title: 'Ссылка погасла',
    // «Жила и вышла» читалось некрологом (правка основателя). Речь о ссылке — так и сказано.
    expired_body: ({ inviter }) =>
      inviter
        ? `Ссылка была доступна ${pluralizeWithCount(INVITE_TTL_DAYS, DAY_FORMS)}, уже истекла. Попроси у ${inviter} новую — это одна кнопка.`
        : `Ссылка была доступна ${pluralizeWithCount(INVITE_TTL_DAYS, DAY_FORMS)}, уже истекла. Попроси новую — это одна кнопка.`,

    revoked_title: 'Приглашение отозвали',
    revoked_body: ({ inviter, gender }) =>
      inviter
        ? textByGender(
            gender,
            `${inviter} забрал{|а} ссылку обратно. Если это недоразумение — попроси новую.`,
            `Ссылку забрали обратно. Если это недоразумение — попроси у ${inviter} новую.`,
          )
        : 'Ссылку забрали обратно. Если это недоразумение — попроси новую.',

    unknown_title: 'Не узнаю эту ссылку',
    unknown_body: 'Похоже, по дороге от неё отвалился хвост. Попроси прислать ещё раз, целиком.',

    self_title: 'Это твоя же ссылка',
    // Второе предложение — не шутка, а предупреждение: переход по своей же ссылке
    // приглашение сжигает, и человек оставался без узы и без понимания, почему.
    self_body: ({ gender }) =>
      textByGender(
        gender,
        'Отправь её тому, с кем хочешь быть рядом. И не переходи по ссылке сам{|а}.',
        'Отправь её тому, с кем хочешь быть рядом. И не переходи по своей же ссылке.',
      ),

    // Единственный исход, который основатель просил называть ошибкой вслух. Ирония выключена:
    // человек не виноват и починить это сам не может.
    limit_inviter_title: ({ inviter }) =>
      inviter ? `У ${inviter} нет свободных нитей` : 'На той стороне нет свободных нитей',
    limit_inviter_body: ({ limit, gender }) =>
      textByGender(
        gender,
        `Больше ${pluralizeWithCount(limit, LINK_GENITIVE_FORMS)} я бесплатно не удержу. Приглашение никуда не делось: как только {он|она} освободит нить или покормит меня, оно сработает.`,
        `Больше ${pluralizeWithCount(limit, LINK_GENITIVE_FORMS)} я бесплатно не удержу. Приглашение никуда не делось: как только нить освободится, оно сработает.`,
      ),

    limit_invitee_title: 'У тебя нет свободных нитей',
    limit_invitee_body: ({ limit }) =>
      `Больше ${pluralizeWithCount(limit, LINK_GENITIVE_FORMS)} я бесплатно не удержу. Освободи одну или покорми меня — приглашение подождёт.`,
    limit_cta: 'Покормить меня 🍖',

    failed_title: 'Не получилось открыть приглашение',
    failed_body: 'Это сломалось у меня, а не у тебя. Ссылка на месте — давай ещё раз.',
    retry: 'Попробовать ещё раз',
    ok: 'Понятно',
    later: 'Потом',
  },
}

export const breakCopy: BreakCopy = {
  title: ({ name }) => `Разорвать нить с ${name}?`,
  body: ({ n, gender }) => {
    const days = pluralizeWithCount(n, DAY_FORMS)
    const tail = `вы перестанете приглядывать друг за другом. Ваши ${days} тоже исчезнут.`
    return textByGender(
      gender,
      `{Он|Она} об этом не узнает — ${tail}`,
      `Об этом никто не узнает — ${tail}`,
    )
  },
  confirm: 'Разорвать',
  cancel: 'Оставить как есть',
}

export const inbox: InboxCopy = {
  // Отклонение от 08_copy.md (правка редактора): «Что прилетело» не читается как название
  // экрана — непонятно, что там внутри. На экране лежат лучики, новые узы и годовщины,
  // поэтому заголовок называет это прямо.
  title: 'Что нового',
  read_all: 'прочитать всё',
  earlier: 'РАНЬШЕ',
  // Род — отправителя лучика. Нейтральный вариант уходит в настоящее время («прилетел
  // лучик»), потому что «послал(а)» — канцелярит, а женская форма по умолчанию и была багом.
  ray: ({ gender }) => textByGender(gender, '✨ послал{|а} тебе лучик', '✨ прилетел лучик'),
  ray_with_text: ({ text, gender }) =>
    textByGender(gender, `✨ послал{|а} лучик: «${text}»`, `✨ прилетел лучик: «${text}»`),
  // Свободный текст стёрт по сроку хранения (legal.md §5) — остаётся факт. Слово «комплимент»
  // здесь не украшение: комплимент у нас и есть текст лучика (спека 04 §5), и оно отличает
  // «человек написал что-то своими словами» от «лучик без слов» строкой выше.
  //
  // Имени в строке нет намеренно, хотя основатель диктовал фразу с именем: в карточке
  // «Что прилетело» имя уже стоит отдельной строкой над текстом события — ровно поэтому его
  // нет ни в `ray`, ни в `ray_with_text`. Строка читается как «Катя · ✨ отправила тебе
  // комплимент», то есть ровно как продиктовано, но без имени дважды подряд.
  ray_erased: ({ gender }) =>
    textByGender(gender, '✨ отправил{|а} тебе комплимент', '✨ тебе прилетел комплимент'),
  link_created: 'теперь вместе ✨',
  milestone: ({ days }) => `💛 Вы вместе ${days}`,
}
